import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, TextField, Select } from '@shopify/polaris';

// Card styling consistent with Store Hours and Notifications
const cardStyle = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  border: '1.5px solid #e0e3ea',
  padding: '20px 24px',
  marginBottom: 24,
  width: '100%'
};

import { authenticatedFetch } from '../utils/api';
import { config } from '../constants';

export default function Faqs({ storeId }) {
  const [search, setSearch] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [modalActive, setModalActive] = useState(false);
  const [type, setType] = useState('qa');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [paragraph, setParagraph] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const API_BASE = config.API_BASE;

  // Fetch FAQs from backend
  const fetchFaqs = async () => {
    if (!storeId) return;
    const params = new URLSearchParams({ storeId });
    const res = await authenticatedFetch(`${API_BASE}/knowledge?${params.toString()}`);
    const data = await res.json();
    setFaqs(data || []);
  };

  useEffect(() => {
    fetchFaqs();
    // eslint-disable-next-line
  }, [storeId]);

  // Add FAQ
  const handleAddFaq = async (faq) => {
    if (faq.type === 'qa') {
      await authenticatedFetch(`${API_BASE}/knowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId, type: 'qa', question: faq.question, answer: faq.answer })
      });
    } else {
      await authenticatedFetch(`${API_BASE}/knowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId, type: 'paragraph', paragraph: faq.paragraph })
      });
    }
    fetchFaqs();
  };

  // Edit FAQ (delete old, add new)
  const handleEditFaq = async (faq, idx) => {
    const rowKey = faqs[idx]?.rowKey || faqs[idx]?.RowKey;
    if (rowKey) {
      await authenticatedFetch(`${API_BASE}/knowledge?id=${rowKey}&storeId=${storeId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
    }
    await handleAddFaq(faq);
  };

  // Delete FAQ
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleDeleteFaq = async (faq, idx) => {
    setPendingDelete({ faq, idx });
  };

  const confirmDeleteFaq = async () => {
    if (!pendingDelete) return;
    const { idx } = pendingDelete;
    const rowKey = faqs[idx]?.rowKey || faqs[idx]?.RowKey;
    if (rowKey) {
      await authenticatedFetch(`${API_BASE}/knowledge?id=${rowKey}&storeId=${storeId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      fetchFaqs();
    }
    setPendingDelete(null);
  };

  const cancelDeleteFaq = () => setPendingDelete(null);

  const handleOpenModal = () => {
    setModalActive(true);
    setType('qa');
    setQuestion('');
    setAnswer('');
    setParagraph('');
    setEditIdx(null);
  };

  // Open modal in edit mode with pre-filled data
  const handleEditModal = (faq, idx) => {
    setModalActive(true);
    setEditIdx(idx);
    setType(faq.type);
    if (faq.type === 'qa') {
      setQuestion(faq.question);
      setAnswer(faq.answer);
      setParagraph('');
    } else {
      setQuestion('');
      setAnswer('');
      setParagraph(faq.paragraph);
    }
  };
  const handleCloseModal = () => {
    setModalActive(false);
    setEditIdx(null);
  };

  const handleSubmit = () => {
    if (type === 'qa' && question && answer) {
      onAdd && onAdd({ type: 'qa', question, answer });
      setModalActive(false);
    } else if (type === 'paragraph' && paragraph) {
      onAdd && onAdd({ type: 'paragraph', paragraph });
      setModalActive(false);
    }
  };

  // Filter FAQs by search string (case-insensitive, matches question/answer/paragraph)
  const filteredFaqs = faqs.filter(faq => {
    const s = search.toLowerCase();
    if (faq.type === 'qa') {
      return (
        faq.question?.toLowerCase().includes(s) ||
        faq.answer?.toLowerCase().includes(s)
      );
    } else {
      return faq.paragraph?.toLowerCase().includes(s);
    }
  });

  return (
    <div style={{ padding: '40px 24px', minHeight: '100vh', background: '#fafbfc' }}>
      <div style={{ width: '100%' }}>
        <div style={{
          background: '#e8f0fe',
          border: '1.5px solid #3b82f6',
          borderRadius: 14,
          padding: '14px 20px',
          marginBottom: 28,
          color: '#174ea6',
          fontSize: 16,
          lineHeight: 1.7,
          boxShadow: '0 2px 10px rgba(37, 99, 235, 0.07)',
          width: '100%',
          fontFamily: 'inherit'
        }}>
          <b style={{ fontWeight: 700, fontSize: 17, display: 'block', marginBottom: 8, color: '#174ea6', letterSpacing: '0.01em' }}>Note</b>
          <div style={{ marginBottom: 6 }}>
            Please avoid adding questions that are dynamic or catalog-specific (e.g., "Do you sell jeans?", "Do you have red dresses?", "What products are available?").
          </div>
          <div style={{ marginBottom: 6 }}>
            These types of questions are answered automatically by the chatbot using live store data and do not need to be included in the training.
          </div>
          <div>
            Focus on adding static, general questions (e.g., "Are you an online store?").
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: 12, borderRadius: 8, border: '1px solid #e3e5e8', fontSize: 16, marginLeft: 4 }}
          />
          <Button variant="primary" onClick={handleOpenModal}>Add FAQ</Button>
        </div>
        {faqs.length === 0 && (
          <div style={{ color: '#aaa', margin: '60px 0', marginLeft: 4 }}>
            No FAQs yet. Click "Add FAQ" to get started.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {filteredFaqs.map((faq, idx) => (
            <Card key={idx} sectioned style={cardStyle}>
              {faq.type === 'qa' ? (
                <>
                  <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 6 }}>{faq.question}</div>
                  <div style={{ color: '#333', fontSize: 16 }}>{faq.answer}</div>
                </>
              ) : (
                <div style={{ color: '#333', fontSize: 16 }}>{faq.paragraph}</div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
                <Button onClick={() => handleEditModal(faq, idx)}>Edit</Button>
                <Button tone="critical" onClick={() => handleDeleteFaq(faq, idx)}>Delete</Button>
              </div>
            </Card>
          ))}
        </div>
        <Modal
          open={!!pendingDelete}
          onClose={cancelDeleteFaq}
          title="Delete FAQ?"
          primaryAction={{ content: 'Delete', destructive: true, onAction: confirmDeleteFaq }}
          secondaryActions={[{ content: 'Cancel', onAction: cancelDeleteFaq }]}
        >
          <Modal.Section>
            <div style={{ fontSize: 16 }}>
              Are you sure you want to delete this FAQ?
              <br />
              <span style={{ color: '#c00', fontWeight: 500 }}>
                This action cannot be undone.
              </span>
            </div>
          </Modal.Section>
        </Modal>
      </div>
      <Modal
        open={modalActive}
        onClose={handleCloseModal}
        title="Add FAQ"
        primaryAction={{ content: editIdx !== null ? 'Update' : 'Add', onAction: () => {
          if (type === 'qa') {
            if (editIdx !== null) {
              handleEditFaq({ type: 'qa', question, answer }, editIdx);
            } else {
              handleAddFaq({ type: 'qa', question, answer });
            }
            setModalActive(false);
            setEditIdx(null);
          } else if (type === 'paragraph') {
            if (editIdx !== null) {
              handleEditFaq({ type: 'paragraph', paragraph }, editIdx);
            } else {
              handleAddFaq({ type: 'paragraph', paragraph });
            }
            setModalActive(false);
            setEditIdx(null);
          }
        } }}
        secondaryActions={[{ content: 'Cancel', onAction: handleCloseModal }]}
      >
        <Modal.Section>
          <Select
            label="Type"
            options={[
              { label: 'Q&A', value: 'qa' },
              { label: 'Paragraph', value: 'paragraph' },
            ]}
            onChange={setType}
            value={type}
          />
          {type === 'qa' ? (
            <>
              <TextField
                label="Question"
                value={question}
                onChange={setQuestion}
                autoComplete="off"
                placeholder="Enter the question"
              />
              <TextField
                label="Answer"
                value={answer}
                onChange={setAnswer}
                autoComplete="off"
                placeholder="Enter the answer"
                multiline
              />
            </>
          ) : (
            <TextField
              label="Paragraph"
              value={paragraph}
              onChange={setParagraph}
              autoComplete="off"
              placeholder="Enter the paragraph"
              multiline
            />
          )}
        </Modal.Section>
      </Modal>
    </div>
  );
}
